import logoPecaAi from '@/assets/images/logo-peca-ai.svg';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { usePecaAiBasketDetail } from '@/core/domain/pecaAi/basket/pecaAiBasket.hooks';
import { useDisclosure } from '@/hooks';
import PecaAiBasketModal from '../PecaAiBasketModal';
import PecaAiOrderModal from '../PecaAiOrderModal';
import { Button, Image, Modal, Tooltip } from 'antd';
import { usePecaAiHasBuyer } from '@/core/domain/pecaAi/pecaAi.hooks';
import { useEnterprisePecaAiToken } from '@/core/domain/enterprise/pecaAi/pecaAi.hooks';
import { getUserFromLocalStorage } from '@/utils/user';
import { useEmployee } from '@/core/domain/enterprise/Employees/Employees.hooks';
import { Group } from '@/components/_common';
import { ExternalLink, PhoneCall } from 'react-feather';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';

export interface PecaAiProduct {
  productId: string;
  amount: number;
  description: string;
  brand?: string;
  image?: string;
  shippingPrice?: number;
  partNumber: string;
  unitPrice: number;
  totalPrice: number;
  deadlineHours?: number | null;
  observation?: string;
  inBasket: boolean;
}

interface PecaAiContextParams {
  orderId?: string;
  basketId?: string;
  products: PecaAiProduct[];
  isLoading: boolean;
  addProduct(newProduct: PecaAiProduct): void;
  removeProduct(productId: string): void;
  postalCode?: string;
  licensePlate?: string;
  cnpj?: string;
}

const PecaAiContext = createContext<PecaAiContextParams>({
  products: [],
  isLoading: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  addProduct: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  removeProduct: () => {},
});

export const usePecaAiContext = () => useContext(PecaAiContext);

export default function PecaAiProvider() {
  const {
    workorder: { data: workorder },
    complement: { data: complement },
    company: { data: company, companySettings },
  } = useWorkorderDetailContext();

  const isAuthorizedToAccessPecaAi =
    !!companySettings?.isAuthorizedToAccessPecaAi;

  const { employeeId } = getUserFromLocalStorage();
  const { data: employee } = useEmployee(Number(employeeId));

  const [products, setProducts] = useState<PecaAiProduct[]>([]);
  const [basketModalOpened, basketModalAction] = useDisclosure(false, {
    onClose: () => {
      setProducts([]);
      basketDetail.remove();
    },
  });
  const [orderModalOpened, orderModalAction] = useDisclosure(false);

  const { isSuccess } = useEnterprisePecaAiToken(isAuthorizedToAccessPecaAi);

  const { isSuccess: hasBuyer, ...pecaAiHasBuyer } = usePecaAiHasBuyer(
    company?.cnpj,
    isSuccess,
  );

  const basketDetail = usePecaAiBasketDetail(
    {
      basketId: complement?.pecaAiBasketId,
      document: company?.cnpj,
    },
    basketModalOpened && hasBuyer,
  );

  const postalCode = useMemo(() => {
    return company?.companyAddress.zip;
  }, [company?.companyAddress]);

  const isLoading = basketDetail.isLoading;

  useEffect(() => {
    if (basketModalOpened) {
      const detail = basketDetail.data?.detail;
      const summary = basketDetail.data?.summary;

      const mapped = detail?.products.map((item) => {
        const match = summary?.products?.find((p) => p.id === item.productId);

        return {
          description: item.description,
          amount: item.amount,
          productId: item.productId,
          totalPrice: item.finalPrice,
          unitPrice: item.unitPrice,
          partNumber: item.partNumber,
          inBasket: true,
          deadlineHours: match?.deadlineHours ?? null,
          brand: match?.brand,
        };
      });

      setProducts(mapped || []);
    }
  }, [basketDetail.dataUpdatedAt, basketModalOpened]);

  const addProduct = useCallback(
    (newProduct: PecaAiProduct) => setProducts((prev) => [...prev, newProduct]),
    [setProducts],
  );

  const removeProduct = useCallback(
    (productId: string) =>
      setProducts((prev) =>
        prev.filter((product) => product.productId !== productId),
      ),
    [setProducts],
  );

  const modalSolicitation = () => {
    Modal.confirm({
      icon: null,

      title: (
        <>
          <Image src={logoPecaAi} preview={false} width={60} />
          <p>Acesso à Integração do Peça Aí</p>
        </>
      ),
      okButtonProps: { style: { display: 'none' } },
      content: (
        <>
          <p>
            <strong>{employee?.name}</strong>, sua empresa ainda não tem
            cadastro no Peça.aí.
          </p>

          <p>
            Cadastre-se agora no link abaixo para iniciar suas compras e ganhe
            descontos exclusivos.
          </p>

          <Group gap={10}>
            <ExternalLink size={16} />
            <Group gap={4}>
              <span>Link:</span>
              <strong>
                <a href="https://peca.ai/" target="_blank">
                  https://peca.ai/
                </a>
              </strong>
            </Group>
          </Group>
          <Group gap={10} style={{ marginBottom: 10 }}>
            <PhoneCall size={16} />
            <Group gap={4}>
              <span>Contato:</span>
              <strong>
                <a href="tel:+5511988135570">11 98813-5570</a>
              </strong>
            </Group>
          </Group>

          <p>Liberação em até 48h úteis após solicitação.</p>
        </>
      ),
    });
  };

  const openBasket = () => {
    if (complement?.pecaAiOrderId) {
      orderModalAction.open();
    } else {
      basketModalAction.open();
    }
  };

  const disablePecaAi =
    complement?.status !== WorkOrderComplementStatus.Open &&
    !complement?.pecaAiBasketId;

  if (!isAuthorizedToAccessPecaAi) return null;

  return (
    <PecaAiContext.Provider
      value={{
        basketId: complement?.pecaAiBasketId,
        orderId: complement?.pecaAiOrderId,
        products,
        isLoading,
        addProduct,
        removeProduct,
        postalCode,
        licensePlate: workorder?.vehicle?.licensePlate,
        cnpj: company?.cnpj,
      }}
    >
      <Tooltip title={disablePecaAi && 'Apenas orçamento Aberto'}>
        <Button
          onClick={hasBuyer ? openBasket : modalSolicitation}
          disabled={disablePecaAi}
          loading={pecaAiHasBuyer.isLoading}
        >
          <Image src={logoPecaAi} preview={false} width={60} />
        </Button>
      </Tooltip>

      <PecaAiBasketModal
        opened={basketModalOpened}
        onClose={basketModalAction.close}
        hasBasket={!!complement?.pecaAiBasketId}
        isLoading={isLoading}
      />

      <PecaAiOrderModal
        opened={orderModalOpened}
        onClose={orderModalAction.close}
      />
    </PecaAiContext.Provider>
  );
}
